/**
 *
 * @page 企业中心/数据看板/管理员/项目状态列表
 *
 **/

import dayjs from 'dayjs';
import { useReducer } from 'react';
import { useRouteMatch, useLocation } from 'react-router-dom';
import { useStoreState } from '&/utils/customHooks';
import { observer } from 'mobx-react';
import BasePageWrap from '&/components/ResourceLib/BasePageWrap';
import { Button, Form, Input, Table, message } from 'antd';
import { Select as AntSelect } from '&/components/AntdEncapsulation';
// import { formatPhone } from '&/utils/common.js';
import { getManegers, getProjects } from '@/api/personCenter/dataBoard';
import OrganizationSelect from '@/components/UserCenter/OrganizationSelect';
import { DAY_OPTIONS, downloadExcel } from './constants';
import { getSelectedManegerLabel, getSelectedDayLabel } from './common';
import './ProjectStatusList.scss';

function ProjectStatusList() {
    const isAdmin = useRouteMatch({
        path: '/user-center/administrator/status-list',
        strict: false, // 全路径?
        sensitive: true, // 大小写?
    });

    const location = useLocation();
    const { search } = location;
    const urlParams = new URLSearchParams(search);
    const initManager = urlParams.get('manager');
    const initStatus = urlParams.get('status');
    const initDays = urlParams.get('days');

    const { entId } = useStoreState('dataBoard');

    const [result, setResult] = useState({
        total: 0,
        list: [],
    });
    const [isLoading, setIsLoading] = useState(false);

    const dftParams = {
        page: 1,
        pageNum: 10,
        days: initDays !== null ? +initDays : 30,
        managerId: initManager,
        keyword: null,
        isAll: 1,
        export: 0,
        status: initStatus !== null ? +initStatus : initStatus,
    };

    if (isAdmin) dftParams.entId = entId;

    const [queryForm, setQueryForm] = useState(dftParams);

    const [didUpdate, forceUpdate] = useReducer(s => s + 1, 0);

    // 初始化调用和entId更新调用
    useEffect(() => {
        if (isAdmin) {
            const next = {
                ...queryForm,
                entId,
                isAll: !entId ? 1 : 2,
            };
            setQueryForm(next);
        }
        forceUpdate();
    }, [entId]);

    const onParamChange = (val, key) => {
        setQueryForm(prev => ({ ...prev, [key]: val, page: 1 }));
        key !== 'keyword' && forceUpdate(); // 防止输入时不停触发搜索
    };

    const handlePageChange = (page, pageNum) => {
        setQueryForm(prev => ({ ...prev, page, pageNum }));
        forceUpdate();
    };

    const getExcelFileName = () => {
        return (
            intl.get('dataBoard.projectStatusStatistics') +
            '-' +
            getSelectedManegerLabel(managerOpts, queryForm.managerId) +
            '-' +
            getSelectedDayLabel(DAY_OPTIONS(intl), queryForm.days) +
            '-' +
            dayjs().format('YYYY_MM_DD')
        );
    };

    const queryList = async () => {
        const PAYLOAD = { ...queryForm };
        !PAYLOAD.entId && delete PAYLOAD.entId;
        const { export: isExport } = PAYLOAD;
        try {
            !isExport && setIsLoading(true);
            const res = await getProjects(PAYLOAD);
            if (isExport) {
                downloadExcel(res, getExcelFileName()).catch(err => {
                    if (err.code === 0) {
                        message.warn('无内容');
                    } else {
                        message.warn(err?.msg);
                    }
                });
            } else {
                setResult({
                    list: res.data.list,
                    total: res.data.page?.totalList,
                });
            }
        } finally {
            setIsLoading(false);
            isExport && setQueryForm(prev => ({ ...prev, export: 0 }));
        }
    };

    useEffect(() => {
        didUpdate > 0 && queryList();
    }, [didUpdate]);

    useEffect(() => {
        queryManagerOptions();
    }, []);

    const dftManeger = {
        value: null,
        label: intl.get('base.all'),
    };
    const [managerOpts, setManagerOpts] = useState([dftManeger]);

    function queryManagerOptions() {
        const PARAMS = {
            // isAll: baseParams.isAll,
            roleId: 2, // 项目经理
        };
        // if (baseParams.entId) {
        //     PARAMS.entId = baseParams.entId;
        // }

        getManegers(PARAMS).then(({ data }) => {
            let opts = [dftManeger];
            for (const el of data.list) {
                opts.push({
                    value: el.userId,
                    label: el.username,
                });
            }
            setManagerOpts(opts);
        });
    }

    const statusList = [
        {
            label: '全部',
            value: null,
        },
        {
            label: '未开始',
            value: 0,
        },
        {
            label: '施工中',
            value: 1,
        },
        {
            label: '待验收',
            value: 2,
        },
        {
            label: '验收通过',
            value: 3,
        },
        {
            label: '已延期',
            value: 4,
        },
    ];

    const calcAddCols = () => {
        const ADD_COLS = [];
        if ([null, 0, 1].includes(queryForm.status)) {
            ADD_COLS.push({
                title: '施工倒计时',
                align: 'right',
                dataIndex: 'reciprocal',
                width: 110,
                render: reciprocal => `${reciprocal}天`,
                sorter: true,
            });
        }
        if ([null, 2, 3, 4].includes(queryForm.status)) {
            ADD_COLS.push({
                title: '延期天数',
                align: 'right',
                dataIndex: 'duty',
                width: 110,
                render: duty => `${duty}天`,
                sorter: true,
            });
        }
        return ADD_COLS;
    };

    const columns = [
        {
            title: intl.get('base.serialNum'),
            key: '1',
            width: 60,
            align: 'center',
            render: (_, __, idx) => {
                return idx + 1 + (queryForm.page - 1) * queryForm.pageNum;
            },
        },
        {
            title: '项目名称',
            dataIndex: 'name',
        },
        {
            title: '项目状态',
            dataIndex: 'statusName',
            width: 150,
            align: 'center',
        },
        {
            title: '工期',
            align: 'center',
            key: '4',
            width: 200,
            render: row => {
                const start = row.timeAt
                        ? dayjs.unix(row.timeAt).format('YYYY.MM.DD')
                        : '',
                    end = row.timeEnd
                        ? dayjs.unix(row.timeEnd).format('YYYY.MM.DD')
                        : '';

                return `${start}~${end}`;
            },
        },
        {
            title: '整体进度',
            align: 'center',
            dataIndex: 'isFinish',
            width: 110,
            render: isFinish => `${isFinish}%`,
            sorter: true,
        },
        ...calcAddCols(),
        {
            title: '项目经理',
            dataIndex: 'managerName',
            width: 200,
            className: 'break-all',
            render: val => val.replaceAll(',', `，`), // 加大间距
        },
        {
            title: '手机号码',
            dataIndex: 'managerMobile',
            align: 'center',
            width: 160,
            render(val) {
                return (
                    <div className="phone-grid">
                        {val.split(',').map((item, index) => {
                            return <p key={index}>{item}</p>;
                        })}
                    </div>
                );
            },
        },
        {
            title: '更新时间',
            dataIndex: 'updateDate',
            align: 'center',
            width: 200,
            render: date =>
                date ? dayjs.unix(date).format('YYYY.MM.DD HH:mm') : '',
        },
    ];
    const { Item } = Form;

    const onExoprt = () => {
        setQueryForm(prev => ({ ...prev, export: 1 }));
        forceUpdate(); // 防止输入时不停触发搜索
    };

    const keyWordItem = (
        <Item label="项目名称">
            <Input.Search
                allowClear
                /* prefix={
                    <i
                        onClick={forceUpdate}
                        className="icon-search filled"
                        style={{ fontSize: '0.75rem' }}
                    />
                } */
                onSearch={forceUpdate}
                style={{
                    width: '11rem',
                }}
                value={queryForm.keyword}
                placeholder="输入项目名称"
                onChange={e =>
                    void onParamChange(e.target.value, 'keyword')
                }></Input.Search>
        </Item>
    );

    const statusItem = (
        <Item label="项目状态">
            <AntSelect
                style={{ width: '10rem' }}
                options={statusList}
                value={queryForm.status}
                placeholder={'请选择'}
                onChange={e => onParamChange(e, 'status')}
            />
        </Item>
    );

    const handleTableChange = (_, __, sorter, { action }) => {
        if (action === 'paginate') return; // 忽略翻页行为
        //  只有排序行为触发onParamChange
        const { field, order } = sorter;
        const SORT_MAP = {
            isFinish: { ascend: 1, descend: 2 },
            reciprocal: { ascend: 3, descend: 4 },
            duty: { ascend: 5, descend: 6 },
        };
        onParamChange(order ? SORT_MAP[field][order] : undefined, 'sort');
    };

    return (
        <>
            {isAdmin && <OrganizationSelect />}
            <BasePageWrap
                title={
                    <strong>
                        {intl.get('dataBoard.projectStatusStatistics')}
                    </strong>
                }
                className="project-status-list"
                type="card"
                headerRight={
                    <Button
                        type="primary"
                        onClick={onExoprt}
                        disabled={!result.list.length}>
                        导出表单
                    </Button>
                }>
                <header className="member-list-form">
                    {keyWordItem}
                    {statusItem}
                    {isAdmin && (
                        <Item label={'项目经理'}>
                            <AntSelect
                                onChange={e => onParamChange(e, 'managerId')}
                                className="manager-select"
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '')
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                                options={managerOpts}
                                value={queryForm.managerId}
                            />
                        </Item>
                    )}
                    <Item label={'时间区间'}>
                        <AntSelect
                            value={queryForm.days}
                            onChange={e => onParamChange(e, 'days')}
                            options={DAY_OPTIONS(intl)}
                        />
                    </Item>
                </header>

                <Table
                    className="normal-table"
                    loading={isLoading}
                    bordered
                    columns={columns}
                    dataSource={result.list}
                    size="small"
                    rowKey="projectId"
                    onChange={handleTableChange}
                    pagination={{
                        size: 'small',
                        current: queryForm.page,
                        onChange: handlePageChange,
                        defaultPageSize: queryForm.pageNum,
                        total: result.total,
                        showSizeChanger: false,
                        style: { marginBottom: 0 },
                        showTotal: total => intl.get('base.total', { total }),
                    }}
                    rowClassName="custom-row"
                />
            </BasePageWrap>
        </>
    );
}

export default observer(ProjectStatusList);
